import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Save,
    Building2,
    MapPin,
    Users,
    Image as ImageIcon,
    X,
    AlertCircle,
    CheckCircle
} from 'lucide-react';
import { useGetApartmentQuery, useUpdateApartmentMutation, useDeleteApartmentImageMutation, Apartment } from '../../store/slices/apiSlice';
import { toast } from 'react-hot-toast';
import { Button } from '../../components/ui/Button';

const EditApartment: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const apartmentId = id ? parseInt(id) : null;
    const isValidId = apartmentId && !isNaN(apartmentId) && apartmentId > 0;

    const { data: apartmentResponse, isLoading, error, refetch } = useGetApartmentQuery(apartmentId!, {
        skip: !isValidId,
        refetchOnMountOrArgChange: true
    });
    const [updateApartment, { isLoading: isSubmitting }] = useUpdateApartmentMutation();
    const [deleteApartmentImage, { isLoading: isDeletingImage }] = useDeleteApartmentImageMutation();
    const [deletingImageIndex, setDeletingImageIndex] = useState<number | null>(null);

    const apartment: Apartment | undefined = apartmentResponse?.data;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        location: '',
        address: '',
        capacity: '',
        amenities: '',
        is_active: true,
        is_available: true,
        images: [] as (File | string)[]
    });

    const [errors, setErrors] = useState<Partial<Record<keyof typeof formData, string>>>({});

    // Populate form with apartment data when loaded
    useEffect(() => {
        if (apartment) {
            setFormData({
                name: apartment.name,
                description: apartment.description || '',
                location: apartment.location,
                address: apartment.address || '',
                capacity: apartment.capacity.toString(),
                amenities: apartment.amenities || '',
                is_active: apartment.is_active,
                is_available: apartment.is_available,
                images: apartment.images || []
            });
        }
    }, [apartment]);

    const handleInputChange = (field: keyof typeof formData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            const fileArray = Array.from(files);
            // For edit, keep existing images (strings) and add new files
            setFormData({
                ...formData,
                images: [...formData.images.filter(img => typeof img === 'string'), ...fileArray]
            });
        }
    };

    const handleRemoveImage = async (index: number) => {
        const image = formData.images[index];
        
        // If it's an existing image (string URL), delete it from server
        if (typeof image === 'string' && apartment) {
            setDeletingImageIndex(index);
            try {
                // Extract filename from URL
                // URL format: API_BASE_URL/uploads/1765636693779-project10.jpg
                // or: /uploads/1765636693779-project10.jpg
                const urlParts = image.split('/');
                const filename = urlParts[urlParts.length - 1];
                
                await deleteApartmentImage({
                    apartment_id: apartment.apartment_id,
                    image_filename: filename
                }).unwrap();
                
                toast.success('Image deleted successfully');
                
                // Remove from form data
                setFormData({
                    ...formData,
                    images: formData.images.filter((_, i) => i !== index)
                });
                
                // Refetch apartment data to get updated images
                refetch();
            } catch (error: any) {
                console.error('Failed to delete image:', error);
                toast.error(error?.data?.message || 'Failed to delete image');
            } finally {
                setDeletingImageIndex(null);
            }
        } else {
            // If it's a new file (File object), just remove from form data
            setFormData({
                ...formData,
                images: formData.images.filter((_, i) => i !== index)
            });
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof typeof formData, string>> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Apartment name is required';
        }

        if (!formData.location.trim()) {
            newErrors.location = 'Location is required';
        }

        if (!formData.capacity || parseInt(formData.capacity) <= 0) {
            newErrors.capacity = 'Capacity is required and must be greater than 0';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (!apartment) return;

        try {
            const payload: any = {
                apartment_id: apartment.apartment_id,
                name: formData.name,
                description: formData.description || null,
                location: formData.location,
                address: formData.address || null,
                capacity: parseInt(formData.capacity),
                amenities: formData.amenities || null,
                is_active: formData.is_active,
                is_available: formData.is_available,
                images: formData.images
            };

            await updateApartment(payload).unwrap();
            toast.success('Apartment updated successfully!');
            navigate('/admin/apartments');
        } catch (error: any) {
            console.error('Failed to update apartment:', error);
            toast.error(error?.data?.message || 'Failed to update apartment');
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading apartment data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Apartment</h2>
                    <p className="text-gray-600 mb-6">
                        {(error as any)?.data?.message || 'There was an error loading the apartment details. Please try again.'}
                    </p>
                    <button
                        onClick={() => navigate('/admin/apartments')}
                        className="text-blue-600 hover:text-blue-800"
                    >
                        ← Back to Apartments
                    </button>
                </div>
            </div>
        );
    }

    if (!apartment) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Apartment Not Found</h2>
                    <p className="text-gray-600 mb-6">The apartment you're looking for doesn't exist.</p>
                    <button
                        onClick={() => navigate('/admin/apartments')}
                        className="text-blue-600 hover:text-blue-800"
                    >
                        ← Back to Apartments
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/apartments')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Edit Apartment</h1>
                                <p className="text-gray-600">Update apartment information</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-4xl mx-auto">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Basic Information */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <Building2 className="w-5 h-5 mr-2" />
                                    Basic Information
                                </h2>
                            </div>
                            <div className="p-6 space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Apartment Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.name ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                        placeholder="e.g., Elegant Mountain View Apartment"
                                    />
                                    {errors.name && (
                                        <p className="mt-1 text-sm text-red-600 flex items-center">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {errors.name}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Apartment description"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Location *
                                        </label>
                                        <div className="relative">
                                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                            <input
                                                type="text"
                                                value={formData.location}
                                                onChange={(e) => handleInputChange('location', e.target.value)}
                                                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.location ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="e.g., Denver"
                                            />
                                        </div>
                                        {errors.location && (
                                            <p className="mt-1 text-sm text-red-600 flex items-center">
                                                <AlertCircle className="w-4 h-4 mr-1" />
                                                {errors.location}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Address
                                        </label>
                                        <div className="relative">
                                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                            <input
                                                type="text"
                                                value={formData.address}
                                                onChange={(e) => handleInputChange('address', e.target.value)}
                                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="e.g., 321 Hilltop Ave, Denver"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Capacity *
                                        </label>
                                        <div className="relative">
                                            <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.capacity}
                                                onChange={(e) => handleInputChange('capacity', e.target.value)}
                                                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.capacity ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="4"
                                            />
                                        </div>
                                        {errors.capacity && (
                                            <p className="mt-1 text-sm text-red-600 flex items-center">
                                                <AlertCircle className="w-4 h-4 mr-1" />
                                                {errors.capacity}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Amenities
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.amenities}
                                        onChange={(e) => handleInputChange('amenities', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="e.g., WiFi, Heating, Kitchen, Parking"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Images */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <ImageIcon className="w-5 h-5 mr-2" />
                                    Images
                                </h2>
                            </div>
                            <div className="p-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Upload New Images
                                    </label>
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        JPG, PNG or GIF. Max size 5MB per image. Leave empty to keep current images.
                                    </p>
                                </div>
                                {formData.images.length > 0 && (
                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Current Images ({formData.images.length})
                                        </label>
                                        <div className="grid grid-cols-4 gap-2">
                                            {formData.images.map((image, index) => (
                                                <div key={index} className="relative">
                                                    {image instanceof File ? (
                                                        <img
                                                            src={URL.createObjectURL(image)}
                                                            alt={`Preview ${index + 1}`}
                                                            className="w-full h-20 object-cover rounded-lg"
                                                        />
                                                    ) : (
                                                        <img
                                                            src={image}
                                                            alt={`Preview ${index + 1}`}
                                                            className="w-full h-20 object-cover rounded-lg"
                                                        />
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveImage(index)}
                                                        disabled={deletingImageIndex === index}
                                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title={typeof image === 'string' ? 'Delete image from server' : 'Remove image'}
                                                    >
                                                        {deletingImageIndex === index ? (
                                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                                        ) : (
                                                            <X className="h-3 w-3" />
                                                        )}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Status Settings */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <CheckCircle className="w-5 h-5 mr-2" />
                                    Status Settings
                                </h2>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="is_active"
                                            checked={formData.is_active}
                                            onChange={(e) => handleInputChange('is_active', e.target.checked)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                                            Active
                                        </label>
                                    </div>

                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="is_available"
                                            checked={formData.is_available}
                                            onChange={(e) => handleInputChange('is_available', e.target.checked)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor="is_available" className="ml-2 block text-sm text-gray-900">
                                            Available
                                        </label>
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-4">
                                    Active: Apartment is visible in the system. Available: Apartment can be booked.
                                </p>
                            </div>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex items-center justify-end space-x-4">
                            <Button
                                variant="outline"
                                onClick={() => navigate('/admin/apartments')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" />
                                        Update Apartment
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditApartment;

